import React, { useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiRefreshCw,
  FiShield,
} from "react-icons/fi";
import { payoutApi } from "./api/payoutApiClient";
import {
  compactBody,
  errorMessage,
  isPayoutMockMode,
  stableIdempotencyKey,
  verifyStepUp,
  writeSessionJson,
} from "./api/payoutMutationHelpers";
import { showPayoutNotification } from "./PayoutNotification";

type ApprovalAction = "approve" | "reject";

const formatAction = (action: ApprovalAction) =>
  action === "approve" ? "Approval" : "Rejection";

function FlowStep({
  index,
  label,
  active,
  complete,
}: {
  index: number;
  label: string;
  active?: boolean;
  complete?: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <span
        className={
          "flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold " +
          (complete || active
            ? "bg-[#1B163B] text-white"
            : "border border-[#D6DCEB] bg-white text-[#505E8E]")
        }
      >
        {complete ? <FiCheck /> : index}
      </span>
      <span
        className={
          "text-sm font-semibold " +
          (active ? "text-[#1B163B]" : "text-[#505E8E]")
        }
      >
        {label}
      </span>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-6 border-b border-[#E0E5F2] py-3 last:border-b-0">
      <span className="text-sm font-medium text-[#505E8E]">{label}</span>
      <div className="max-w-[220px] text-right text-sm font-semibold text-[#1B163B]">
        {value}
      </div>
    </div>
  );
}

function PayoutApprovalActionVerify() {
  const navigate = useNavigate();
  const { action: actionParam } = useParams();
  const [searchParams] = useSearchParams();
  const action: ApprovalAction = actionParam === "reject" ? "reject" : "approve";
  const transferId = searchParams.get("transferId") || "-";
  const beneficiaryName = searchParams.get("beneficiary") || "-";
  const transferAmount = searchParams.get("amount") || "-";
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const method = "authenticator" as const;
  const [reason, setReason] = useState("");
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const code = useMemo(() => otp.join(""), [otp]);
  const ready = code.length === 6 && (action === "approve" || reason.trim().length > 0);
  const [submitting, setSubmitting] = useState(false);
  const [, setSubmitError] = useState("");
  const showSubmitError = React.useCallback((message: string) => {
    setSubmitError(message);
    showPayoutNotification({ tone: "error", message });
  }, []);
  const instruction = "Enter the 6-digit code from your Google Authenticator app.";

  const updateDigit = (index: number, value: string) => {
    const nextValue = value.replace(/\D/g, "").slice(-1);
    const nextOtp = [...otp];
    nextOtp[index] = nextValue;
    setOtp(nextOtp);

    if (nextValue && index < inputsRef.current.length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 6).split("");
    if (!digits.length) return;
    const nextOtp = ["", "", "", "", "", ""];
    digits.forEach((digit, index) => {
      nextOtp[index] = digit;
    });
    setOtp(nextOtp);
    inputsRef.current[Math.min(digits.length, 5)]?.focus();
  };

  const confirmTo =
    `/payouts/transfers/pending-approval/${action}/confirm?transferId=${encodeURIComponent(
      transferId,
    )}` + (action === "reject" ? `&reason=${encodeURIComponent(reason.trim())}` : "");

  const handleVerifyAction = async () => {
    setSubmitError("");
    if (!ready) {
      showSubmitError(
        action === "reject"
          ? "Reject transfer: enter the 6-digit Google Authenticator code and rejection reason to continue."
          : "Approve transfer: enter the 6-digit Google Authenticator code to continue.",
      );
      return;
    }
    if (submitting) return;

    if (isPayoutMockMode()) {
      navigate(confirmTo);
      return;
    }

    setSubmitting(true);
    try {
      const stepUpToken = await verifyStepUp({
        purpose: action === "approve" ? "APPROVE_PAYOUT" : "REJECT_PAYOUT",
        method,
        code,
        subjectId: transferId,
      });
      const body = compactBody({
        reason: action === "reject" ? reason.trim() : undefined,
        decision: action === "approve" ? "APPROVED" : "REJECTED",
      });
      const response =
        action === "approve"
          ? await payoutApi.approvePayment(
              transferId,
              body,
              stableIdempotencyKey("approve-payment", transferId),
              stepUpToken,
            )
          : await payoutApi.rejectPayment(
              transferId,
              body,
              stableIdempotencyKey("reject-payment", transferId),
              stepUpToken,
            );
      writeSessionJson(`payoutApprovalActionResult:${transferId}`, response);
      navigate(confirmTo);
    } catch (error) {
      showSubmitError(errorMessage(error, `${action === "approve" ? "Approve" : "Reject"} transfer: unable to ${action} this transfer. Please try again.`));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7">
        <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
          Verify {formatAction(action)}
        </h1>
        <p className="mt-1 text-sm font-medium text-[#505E8E]">
          Verify this payout transfer before the {action} action is submitted.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-8">
        <FlowStep index={1} label="Request Details" complete />
        <span className="h-px w-16 bg-[#D6DCEB]" />
        <FlowStep index={2} label="Verify" active />
        <span className="h-px w-16 bg-[#D6DCEB]" />
        <FlowStep index={3} label="Confirm" />
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,760px)_380px]">
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <div className="mb-6 flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[8px] bg-[#1B163B0D] text-xl text-[#1B163B]">
              <FiShield />
            </div>
            <div>
              <h2 className="text-[18px] font-semibold text-[#1B163B]">
                Verify Transfer {formatAction(action)}
              </h2>
              <p className="mt-1 text-sm font-medium text-[#505E8E]">
                {instruction}
              </p>
            </div>
          </div>

          <div>
            <div className="rounded-[8px] border border-[#1B163B] bg-[#1B163B0D] px-5 py-4 text-left">
              <div className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-[6px] bg-[#DCFCE7] text-[#16A34A]">
                    <FiShield />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-[#1B163B]">
                      Google Authenticator
                    </span>
                    <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                      Recommended for approval actions
                    </span>
                  </span>
                </span>
                <FiCheck className="text-lg text-[#16A34A]" />
              </div>
            </div>
          </div>

          <div className="mt-8">
            <label className="mb-3 block text-sm font-semibold text-[#1B163B]">
              Verification Code
            </label>
            <div className="flex flex-wrap gap-3">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(element) => {
                    inputsRef.current[index] = element;
                  }}
                  value={digit}
                  onChange={(event) => updateDigit(index, event.target.value)}
                  onPaste={(event) => {
                    event.preventDefault();
                    handlePaste(event.clipboardData.getData("text"));
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Backspace" && !otp[index] && index > 0) {
                      inputsRef.current[index - 1]?.focus();
                    }
                  }}
                  inputMode="numeric"
                  className="h-12 w-12 rounded-[6px] border border-[#E0E5F2] bg-white text-center text-lg font-semibold text-[#1B163B] outline-none focus:border-[#1B163B]"
                />
              ))}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-5 text-sm font-semibold">
              <button
                type="button"
                onClick={() => setOtp(["", "", "", "", "", ""])}
                className="flex items-center gap-2 text-[#1B163B]"
              >
                <FiRefreshCw />
                Resend code
              </button>
            </div>
          </div>

          {action === "reject" && (
            <div className="mt-8">
              <label className="mb-3 block text-sm font-semibold text-[#1B163B]">
                Rejection Reason <span className="text-[#EF4444]">*</span>
              </label>
              <textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                maxLength={250}
                rows={4}
                className="w-full resize-none rounded-[6px] border border-[#E0E5F2] bg-white px-4 py-3 text-sm font-semibold text-[#1B163B] outline-none placeholder:text-[#A3AED0] focus:border-[#1B163B]"
                placeholder="Enter reason for rejecting this transfer"
              />
              <div className="mt-2 flex items-center justify-between text-xs font-medium text-[#505E8E]">
                <span>This reason will be shared with the requester.</span>
                <span>{reason.length} / 250</span>
              </div>
            </div>
          )}

          <div className="mt-8 rounded-[6px] bg-[#1B163B0D] px-5 py-4 text-sm font-medium text-[#1B163B]">
            This security check confirms that the checker or approver is
            authorizing the selected action.
          </div>
          <div className="mt-7 flex flex-wrap items-center justify-between gap-4 border-t border-[#E0E5F2] pt-5">
            <Link
              to="/payouts/transfers/pending-approval"
              className="flex h-10 items-center justify-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-7 text-sm font-semibold text-[#1B163B]"
            >
              <FiArrowLeft />
              Back
            </Link>
            <button
              type="button"
              onClick={handleVerifyAction}
              disabled={submitting}
              style={{
                backgroundColor: "#1B163B",
                borderColor: "#1B163B",
                color: "#FFFFFF",
              }}
              className="flex h-10 min-w-[190px] items-center justify-center gap-3 rounded-[6px] border px-7 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span style={{ color: "#FFFFFF" }}>
                {submitting ? "Submitting..." : "Verify & Continue"}
              </span>
              {!submitting && <FiArrowRight style={{ color: "#FFFFFF" }} />}
            </button>
          </div>
        </section>

        <aside className="h-fit rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <h2 className="text-[18px] font-semibold text-[#1B163B]">
            Approval Summary
          </h2>
          <div className="my-5 h-px bg-[#E0E5F2]" />
          <SummaryRow label="Transfer ID" value={transferId} />
          <SummaryRow label="Beneficiary" value={beneficiaryName} />
          <SummaryRow label="Transfer Amount" value={transferAmount} />
          <SummaryRow
            label="Action"
            value={action === "approve" ? "Approve Transfer" : "Reject Transfer"}
          />
          {action === "reject" && (
            <SummaryRow
              label="Reason"
              value={reason.trim() || "Required before rejecting"}
            />
          )}
          <SummaryRow label="Verification" value="Google Authenticator" />
          <div className="mt-5 rounded-[6px] bg-[#DCFCE7] px-4 py-3 text-sm font-medium text-[#166534]">
            The transfer remains locked while verification is in progress.
          </div>
        </aside>
      </div>
    </div>
  );
}

export default PayoutApprovalActionVerify;
